import { useState, useEffect, useRef } from "react";
import { fetchGlobalAdminsConfig, fetchAutomationConfigsForBootstrap, fetchTenantDataRow, fetchProvisionedProfile, upsertProvisionedProfile } from "../lib/appBootstrapData";
import { supabase } from "../supabase";
import pms, { setAllowedProperties } from "../services/pms";
import { queryClient } from "../lib/queryClient";
import { useAuth } from "../hooks/useAuth";
import { logGlobalAction } from "../constants";
import type { AppUser } from "../hooks/useHRData";

/** Shape of a tenants-table data JSONB blob relevant to the app shell. */
interface TenantConfig {
  allowedProperties?: Array<string | { id?: string }>;
  features?: Record<string, boolean>;
  onboardingCompleted?: boolean;
  customIframeName?: string;
  [key: string]: unknown;
}

/**
 * Root authentication & session hook for AdaptHub.
 *
 * Owns all Supabase-Auth-based session state: user/role resolution, Apaleo
 * token exchange, tenant config + subscription status, and the initial
 * loading gate. Delegates realtime concerns (offline state, force-reload) to
 * `useAppRealtime`.
 *
 * @returns {Object} Auth state + setters consumed by `AppRouter`.
 */
export function useAppAuth() {
  const [user, setUser] = useState<AppUser | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [isApaleoAuthed, setIsApaleoAuthed] = useState(false);
  const [loadingToken, setLoadingToken] = useState(true);
  const isInitialLoad = useRef(true);
  const unsubTenantRef = useRef<(() => void) | null>(null);
  // Cached UNFILTERED property ids from the last successful fetch — lets the
  // property-population effect re-apply the tenant intersection when
  // tenantConfig arrives late, and lets the retry loop early-exit once the
  // fetch succeeded (populated ≠ the filtered list being non-empty).
  const unfilteredPropsRef = useRef<Array<{ id: string }>>([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState("active");
  const [tenantConfig, setTenantConfig] = useState<TenantConfig | null>(null);
  const [globalAdmins, setGlobalAdmins] = useState<string[]>([]);

  const { isLoaded, userId, getToken, user: authUser, signOut } = useAuth();

  useEffect(() => {
    if (isLoaded) {
      pms.registerTokenGetter(getToken);
    }
  }, [isLoaded, getToken]);

  // --- APALEO NATIVE LOGIN CUSTOM TOKEN RESOLUTION ---
  useEffect(() => {
    // Read from URL fragment (#) instead of query param (?) for security
    // Fragments are never sent to servers, avoiding log/Referer leakage
    const hash = window.location.hash.replace(/^#/, '');
    const params = new URLSearchParams(hash);
    const apaleoLogin = params.get("apaleo_login");
    if (apaleoLogin === "success") {
      localStorage.setItem("adapthub_login_method", "apaleo");
      // Clear the hash — user will be presented with Supabase sign-in
      window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
    }
  }, []);

  const hasLoggedLogin = useRef(false);

  useEffect(() => {
    if (user && !hasLoggedLogin.current) {
      logGlobalAction(
        user,
        "System-Login",
        "Benutzer hat sich erfolgreich angemeldet.",
      );
      hasLoggedLogin.current = true;
    }
    if (!user) {
      hasLoggedLogin.current = false;
    }
  }, [user]);

  // Supabase Auth manages sessions natively; tenant/role come from app_metadata.
  // No external metadata sync is required.

  // Supabase Auth-based session management
  useEffect(() => {
    if (!isLoaded) return;

    let cancelled = false;
    const userChannel = null;

    const initSession = async () => {
      // Clean up previous tenant subscriptions to prevent memory leaks
      if (unsubTenantRef.current) {
        unsubTenantRef.current();
        unsubTenantRef.current = null;
      }

      if (userId && authUser) {
        // Supabase Auth session is managed natively by supabase-js (src/supabase.js).

        if (isInitialLoad.current) {
          setLoadingToken(true);
        }

        // Read tenant/role from app_metadata (set during the Supabase Auth migration).
        const activeTenantId = authUser.app_metadata?.tenant_id || '';
        const roleClaim = authUser.app_metadata?.role || '';
        const isGuestUser = roleClaim === 'guest';
        const emailClaim = authUser.email || '';
        // resolvedUid tracks the Supabase users.id (legacy_user_id). For new users,
        // legacy_user_id === Supabase Auth UUID (authUser.id).
        const resolvedUid = authUser.app_metadata?.legacy_user_id || authUser.id;

        // tenant/role are read directly from app_metadata — no sign-time resolution needed.

        Promise.resolve(fetchGlobalAdminsConfig().then(({ data: row }) => {
          if (row && Array.isArray(row.data?.globalAdmins)) {
            setGlobalAdmins(row.data.globalAdmins);
          }
        })).catch(err => console.warn("Failed to fetch global admins:", err));

        // Live subscribe to active SaaS tenant configuration doc
        if (activeTenantId) {
          const fetchTenant = async () => {
            // Bounded boot-path read (AGENTS.md §1): tenant automation configs are
            // a small fixed set; the cap keeps the session bootstrap predictable.
            const { data: tenantRow } = await fetchAutomationConfigsForBootstrap(activeTenantId);
            // Also read tenant-level data (allowedProperties, features, etc.) from tenants table
            const { data: tenantData } = await fetchTenantDataRow(activeTenantId);
            const tenantCfg = (tenantData?.data || {}) as Record<string, unknown>;
            if (tenantRow && tenantRow.length > 0) {
              const automations: Record<string, unknown> = {};
              tenantRow.forEach((r: { id: string; config?: unknown }) => { automations[r.id] = r.config; });
              setTenantConfig({ ...tenantCfg, automations, onboardingCompleted: true });
              setSubscriptionStatus('active');
            } else {
              setTenantConfig({ ...tenantCfg, onboardingCompleted: false });
              setSubscriptionStatus('active');
            }
          };
          fetchTenant();
          // Realtime handled by centralized realtimeStore
        }

        // Get Apaleo token using the shared refresh lock (prevents 429 rate limiting)
        try {
          const accessToken = await (pms as any).refreshAccessToken();
          if (accessToken) {
            pms.setToken(accessToken);
          }
        } catch (err) {
          console.error("Token-Abruf gescheitert:", err);
        }

        const legacyUserId = resolvedUid;
        const fallbackEmail = emailClaim || authUser.email || "";
        const fallbackName =
          `${authUser.user_metadata?.first_name || ''} ${authUser.user_metadata?.last_name || ''}`.trim() ||
          fallbackEmail.split("@")[0].split(/[._-]/).map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(" ") ||
          "Kollege";

        if (!activeTenantId) {
          setUser({
            name: fallbackName,
            id: legacyUserId,
            email: fallbackEmail,
            role: "pending",
            tenantId: null,
            preferredProperties: [],
            menuGroups: [],
            department: "",
            loyaltyPoints: 0,
          });
          setUserRole("pending");
          setIsApaleoAuthed(true);
          setLoadingToken(false);
          isInitialLoad.current = false;
          return;
        }

        // Scope the user profile: guests under guests table, others under users table
        const tableName = isGuestUser ? 'guests' : 'users';
        try {
          const { data: currentRow } = await fetchProvisionedProfile(tableName, activeTenantId, legacyUserId);
          const docExists = !!currentRow;
          const currentData = currentRow || {};
          const currentRole = isGuestUser ? "guest" : (currentData.role || roleClaim || "pending");

          let currentName = fallbackName;
          if (isGuestUser) {
            if (currentData.first_name) {
              currentName = `${currentData.first_name} ${currentData.last_name || ''}`.trim();
            } else {
              currentName = `${authUser.user_metadata?.first_name || ''} ${authUser.user_metadata?.last_name || ''}`.trim() || fallbackName;
            }
          } else {
            // Prefer Supabase user_metadata name over DB name column (which may contain email username)
            const fullName = `${authUser.user_metadata?.first_name || ''} ${authUser.user_metadata?.last_name || ''}`.trim();
            currentName = fullName || currentData.name || currentData.data?.name || currentData.data?.displayName || fallbackName;
          }
          const currentEmail = currentData.email || fallbackEmail;

          const needsEmailUpdate = !currentData.email && currentEmail;
          const needsGuestNameUpdate = isGuestUser && (
            (!currentData.first_name && authUser.user_metadata?.first_name) ||
            (!currentData.last_name && authUser.user_metadata?.last_name)
          );
          const needsStatusUpdate = isGuestUser && (!currentData.status || currentData.status === 'unregistered');

          if (!docExists || isInitialLoad.current || needsEmailUpdate || needsGuestNameUpdate || needsStatusUpdate) {
            if (isGuestUser && !docExists) {
              return;
            }
            const updatePayload: Record<string, any> = {
              last_login: new Date().toISOString(),
            };
            if (!docExists) {
              updatePayload.email = currentEmail;
              if (isGuestUser) {
                updatePayload.first_name = authUser.user_metadata?.first_name || "";
                updatePayload.last_name = authUser.user_metadata?.last_name || "";
                updatePayload.data = { loyaltyPoints: 0, status: "registered" };
              } else {
                updatePayload.name = currentName;
                updatePayload.role = currentRole;
              }
            } else {
              if (isGuestUser) {
                if (!currentData.status || currentData.status === 'unregistered') {
                  updatePayload.status = "registered";
                }
                if (authUser.user_metadata?.first_name) {
                  updatePayload.first_name = currentData.first_name || authUser.user_metadata?.first_name;
                }
                if (authUser.user_metadata?.last_name) {
                  updatePayload.last_name = currentData.last_name || authUser.user_metadata?.last_name;
                }
              } else {
                if (!currentData.name) {
                  updatePayload.name = currentName;
                }
              }
              if (currentEmail) {
                updatePayload.email = currentData.email || currentEmail;
              }
            }
            await upsertProvisionedProfile(tableName, { id: legacyUserId, tenant_id: activeTenantId, ...updatePayload });
          }
        } catch (err) {
          console.error(err);
        }

        const refreshUser = async () => {
          const { data: row } = await fetchProvisionedProfile(tableName, activeTenantId, legacyUserId);
          if (row) {
            const data = row;
            const resolvedRole = isGuestUser ? "guest" : (data.role || "pending");
            const userDept = isGuestUser ? "" : (
              data.department ||
              (resolvedRole === "admin" || resolvedRole === "management" ? "MG"
                : resolvedRole === "sales" ? "SL"
                  : resolvedRole === "reception" ? "FO"
                    : resolvedRole === "rads" ? "IR"
                      : resolvedRole === "housekeeping" || resolvedRole === "hausdame" ? "HK"
                        : resolvedRole === "haustechnik" || resolvedRole === "ht_leitung" ? "HT" : "")
            );
            setUser({
              name: isGuestUser ? (data.first_name ? `${data.first_name} ${data.last_name || ''}`.trim() : fallbackName) : (data.name || data.data?.name || data.data?.displayName || fallbackName),
              id: legacyUserId,
              email: data.email || fallbackEmail,
              role: resolvedRole,
              preferredProperties: data.preferred_properties || [],
              menuGroups: data.menu_groups || [],
              department: userDept,
              anspruch: data.anspruch !== undefined ? data.anspruch : 30,
              vorjahr: data.vorjahr !== undefined ? data.vorjahr : 5,
              targetHoursPerDay: data.target_hours_per_day !== undefined ? Number(data.target_hours_per_day) : 8,
              workdays: data.workdays || [true, true, true, true, true, false, false],
              holidayState: data.data?.holidayState || data.holidayState || 'BE',
              tenantId: activeTenantId,
              loyaltyPoints: data.loyalty_points || 0,
            });
            setUserRole(resolvedRole);
          } else {
            setUser({
              name: fallbackName,
              id: legacyUserId,
              email: fallbackEmail,
              role: isGuestUser ? "guest" : "pending",
              preferredProperties: [],
              menuGroups: [],
              department: "",
              tenantId: activeTenantId,
              loyaltyPoints: 0,
            });
            setUserRole(isGuestUser ? "guest" : "pending");
          }
          setIsApaleoAuthed(true);
          setLoadingToken(false);
          isInitialLoad.current = false;
        };

        // Initial load
        refreshUser();

        // Realtime handled by centralized realtimeStore
      } else {
        if (cancelled) return;
        setIsApaleoAuthed(false);
        setUser(null);
        setUserRole(null);
        setSubscriptionStatus('active');
        setLoadingToken(false);
        isInitialLoad.current = false;
        pms.setToken(null as unknown as string);
      }
    };

    initSession();

    return () => {
      cancelled = true;
      if (unsubTenantRef.current) unsubTenantRef.current();
      if (userChannel) supabase.removeChannel(userChannel);
    };
  }, [userId, isLoaded, authUser, getToken]);

  // Property population with RESILIENCE (fix: users on phones lost all Standort
  // dropdowns for a whole session after ONE transient network failure).
  //
  // Root cause: the previous one-shot fetch had no retry and fetchProperties()
  // swallows every error to [] — a single failed /api/token refresh or a dead
  // cellular moment at app open left ALLOWED_PROPERTIES empty forever (the
  // effect deps never changed again). Desktop/wifi sessions usually succeed on
  // the first try, which is why the bug was phone-heavy and intermittent.
  //
  // Fix: (1) bounded exponential-backoff retries (1s→16s, 5 fetch attempts), (2)
  // self-heal via `online` + `visibilitychange` listeners (phones resuming
  // from background), (3) re-filter cached unfiltered ids when tenantConfig
  // arrives late (previously a fast property fetch before tenant config could
  // skip the tenant intersection entirely), (4) reset on sign-out.
  useEffect(() => {
    if (!isApaleoAuthed) {
      unfilteredPropsRef.current = [];
      return;
    }
    if (userRole === 'guest') return;

    const MAX_ATTEMPTS = 5;
    const MAX_DELAY_MS = 16000;
    let attempt = 0;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const applyIds = (props: Array<{ id: string }>): boolean => {
      unfilteredPropsRef.current = props;
      let ids = props.map((p: { id: string }) => p.id);
      if (tenantConfig && tenantConfig.allowedProperties && tenantConfig.allowedProperties.length > 0) {
        const allowedUpper = tenantConfig.allowedProperties.map((p: string | { id?: string }) =>
          typeof p === 'string' ? p.toUpperCase() : (p.id || '').toUpperCase()
        );
        ids = ids.filter((id: string) => allowedUpper.includes(id.toUpperCase()));
      }
      // Exclude Apaleo sandbox properties (3-letter codes like BER, LND).
      // Real production properties are 5+ chars (BERLIN, GIESSEN, SIEGEN, WETZLAR).
      ids = ids.filter((id: string) => (id || '').length > 3);
      if (ids.length > 0) {
        setAllowedProperties(ids);
        // Invalidate queries that ran with an empty ALLOWED_PROPERTIES so
        // they refetch now that the list is populated. This is required
        // because ALLOWED_PROPERTIES is a module-level `let` — mutating it
        // does NOT re-render consumers, so an `enabled` gate alone can never
        // flip. The Haustechnik tabs (Zimmercheck / Freie Zimmer /
        // Wartungsfenster) throw when the list is empty precisely so this
        // invalidation retries them instead of caching an empty result.
        queryClient.invalidateQueries({ queryKey: ['reservations'] });
        queryClient.invalidateQueries({ queryKey: ['apaleo', 'units'] });
        queryClient.invalidateQueries({ queryKey: ['apaleo', 'maintenances'] });
        return true;
      }
      return false;
    };

    const isPopulated = () => unfilteredPropsRef.current.length > 0;

    const attemptFetch = async () => {
      if (cancelled || isPopulated()) return;
      try {
        const props = await pms.fetchProperties() as Array<{ id: string }>;
        if (cancelled) return;
        if (applyIds(props)) return;
        // fetchProperties() swallows errors to [] — treat an empty result the
        // same as a thrown error: fall through to the retry schedule below.
      } catch (err) {
        console.error("Fehler beim dynamischen Laden der Properties:", err);
      }
      if (attempt >= MAX_ATTEMPTS || cancelled) return;
      const delay = Math.min(1000 * Math.pow(2, attempt), MAX_DELAY_MS);
      attempt++;
      retryTimer = setTimeout(() => { void attemptFetch(); }, delay);
    };

    // Late tenantConfig: the property fetch may have succeeded BEFORE the
    // tenant config loaded — re-run the intersection with the cached list.
    if (unfilteredPropsRef.current.length > 0) {
      applyIds(unfilteredPropsRef.current);
    } else {
      void attemptFetch();
    }

    // Self-heal for phones: when connectivity returns or the app resumes from
    // background, retry while the list is still empty. Both listeners no-op
    // once populated (attemptFetch early-returns). The backoff counter resets
    // per reconnect so a user regaining connectivity gets a fresh retry cycle.
    const retryOnReconnect = () => {
      if (isPopulated() || cancelled) return;
      attempt = 0;
      void attemptFetch();
    };
    window.addEventListener('online', retryOnReconnect);
    window.addEventListener('visibilitychange', retryOnReconnect);

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      window.removeEventListener('online', retryOnReconnect);
      window.removeEventListener('visibilitychange', retryOnReconnect);
    };
  }, [isApaleoAuthed, tenantConfig, userRole]);

  return {
    isLoaded,
    userId,
    hasSession: !!userId,
    user,
    userRole,
    isApaleoAuthed,
    setIsApaleoAuthed,
    loadingToken,
    subscriptionStatus,
    tenantConfig,
    setTenantConfig,
    globalAdmins,
    signOut,
    getToken,
  };
}

